"use client";

import React, { useEffect } from "react";

import { X } from "lucide-react";

import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

interface DebugJsonModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: unknown;
  title?: string;
}

const DebugJsonModal: React.FC<DebugJsonModalProps> = ({
  isOpen,
  onClose,
  data,
  title = "Product JSON Data",
}) => {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useBodyScrollLock({ key: "debug-json-modal", enabled: isOpen });

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex h-full max-h-[95vh] w-full max-w-7xl flex-col rounded-lg bg-gray-900 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-lg border-b border-gray-700 bg-gray-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-3 w-3 rounded-full bg-red-500" />
            <div className="h-3 w-3 rounded-full bg-yellow-500" />
            <div className="h-3 w-3 rounded-full bg-green-500" />
            <h2 className="ml-2 font-mono text-lg font-semibold text-white">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
            aria-label="Close JSON viewer"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* JSON Content */}
        <div className="flex-1 overflow-auto p-6 font-mono text-sm">
          <pre className="whitespace-pre-wrap break-words leading-relaxed text-green-400">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between rounded-b-lg border-t border-gray-700 bg-gray-800 px-6 py-3 text-xs text-gray-400">
          <div className="flex items-center gap-4">
            <span>🤖 Debug Mode</span>
            <span>Press ESC to close</span>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(data, null, 2));
              // Could add a toast notification here
            }}
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700"
          >
            Copy JSON
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebugJsonModal;
